import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "@/components/ModeToggle";
import { cn } from "@/lib/utils";
import logo from "@/assets/optibiz-logo-landscape1.svg";

const Header = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToSection = (id: string) => {
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => {
                const element = document.getElementById(id);
                if (element) element.scrollIntoView({ behavior: "smooth" });
            }, 100);
        } else {
            const element = document.getElementById(id);
            if (element) element.scrollIntoView({ behavior: "smooth" });
        }
        setIsMobileMenuOpen(false);
    };

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ?
                'bg-background/80 border-b border-border/40 shadow-lg backdrop-blur-sm' :
                'bg-transparent'
                }`}
        >
            <div className="container mx-auto px-4 md:px-8 flex items-center justify-between py-2 md:py-3">
                {/* Logo */}
                <Link to="/" className="flex items-center gap-3 group relative">
                    <img src={logo} alt="OptiBiz Suite" className="h-18 sm:h-16 md:h-16 transition-all duration-300 dark:[filter:brightness(0)_invert(1)]" />
                    <div className="absolute -inset-3 bg-gradient-to-r from-optibiz-blue/20 via-optibiz-green/20 to-optibiz-orange/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl animate-pulse"></div>
                </Link>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center font-bold gap-8">
                    <Link to="/" className="text-sm font-bold text-gray-600 dark:text-gray-300 hover:text-optibiz-blue dark:hover:text-optibiz-yellow transition-colors relative group">
                        Home
                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-optibiz-blue to-optibiz-orange group-hover:w-full transition-all duration-300"></span>
                    </Link>
                    <Link to="/applications" className="text-sm font-bold text-gray-600 dark:text-gray-300 hover:text-optibiz-blue dark:hover:text-optibiz-yellow transition-colors relative group">
                        Applications
                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-optibiz-blue to-optibiz-orange group-hover:w-full transition-all duration-300"></span>
                    </Link>
                    <Link to="/contact" className="text-sm font-bold text-gray-600 dark:text-gray-300 hover:text-optibiz-blue dark:hover:text-optibiz-yellow transition-colors relative group">
                        Contact
                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-optibiz-blue to-optibiz-orange group-hover:w-full transition-all duration-300"></span>
                    </Link>
                </div>

                {/* CTA & Theme Toggle */}
                <div className="hidden md:flex items-center gap-4">
                    <ModeToggle />
                    <Link to="/contact">
                        <Button className="bg-gradient-to-r from-optibiz-blue via-optibiz-green to-optibiz-orange dark:from-optibiz-orange dark:via-optibiz-yellow dark:to-optibiz-green text-white font-bold hover:from-optibiz-blue/90 hover:via-optibiz-green/90 hover:to-optibiz-orange/90 dark:hover:from-optibiz-orange/90 dark:hover:via-optibiz-yellow/90 dark:hover:to-optibiz-green/90 rounded-full px-6 transition-all relative overflow-hidden group">
                            <span className="relative z-10">Request a Demo</span>
                            <div className="absolute inset-0 bg-gradient-to-r from-optibiz-yellow/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </Button>
                    </Link>
                </div>

                {/* Mobile Menu Button */}
                <div className="md:hidden flex items-center gap-2">
                    <ModeToggle />
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="p-2 text-tasktide-dark dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                    >
                        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="absolute font-bold top-full left-0 right-0 mt-2 mx-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg p-4 flex flex-col gap-4 md:hidden border border-optibiz-blue/20 dark:border-optibiz-blue/30 rounded-xl animate-fade-in-up">
                    <Link
                        to="/"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="text-sm font-bold text-gray-600 dark:text-gray-300 p-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg"
                    >
                        Home
                    </Link>
                    <Link
                        to="/applications"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="text-sm font-bold text-gray-600 dark:text-gray-300 p-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg"
                    >
                        Applications
                    </Link>
                    <Link
                        to="/contact"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="text-sm font-bold text-gray-600 dark:text-gray-300 p-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg"
                    >
                        Contact
                    </Link>
                    <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                        <Button className="bg-gradient-to-r from-optibiz-blue via-optibiz-green to-optibiz-orange dark:from-optibiz-orange dark:via-optibiz-yellow dark:to-optibiz-green text-white w-full rounded-full font-bold hover:from-optibiz-blue/90 hover:via-optibiz-green/90 hover:to-optibiz-orange/90 dark:hover:from-optibiz-orange/90 dark:hover:via-optibiz-yellow/90 dark:hover:to-optibiz-green/90 transition-all">
                            Request a Demo
                        </Button>
                    </Link>
                </div>
            )}
        </nav>
    );
};

export default Header;
